import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProject, selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import TextAreaAutosize from 'react-textarea-autosize';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import SessionSelect from '@ui/session-select/session-select';
import toastTrigger from '@ui/toast/toast-trigger';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type SessionMetadataModalProps = {
  open: boolean;
  handleClose: () => void;
  sessions?: number[];
  metadata?: string;
};

const SessionMetadataModal = ({ open, sessions, metadata, handleClose }: SessionMetadataModalProps) => {
  const project = useAppSelector(getWorkingProject);
  const isSaving = useAppSelector(selectIsSavingWorkingProject);

  const [localMetadata, setLocalMetadata] = useState(metadata);
  const maxLength = 100;

  const [showSessionSelect, setShowSessionSelect] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState(sessions);

  const dispatch = useAppDispatch();

  const onClose = () => {
    if (isSaving) return;
    handleClose();
  };

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newText = evt.target.value;
    setLocalMetadata(newText);
  };

  const handleSave = async () => {
    if (isSaving || !project) return;
    try {
      const sessionsToSave = project.sessions?.map((session) => ({
        uuid: session.uuid,
        internal_descriptor: selectedSessions?.includes(session.session) ? localMetadata : null,
      }));

      dispatch(
        saveWorkingProjectByFields({
          saveId: 'settings-session-metadata',
          sessionsToSave,
        })
      );
    } catch (error) {
      console.error(error);
      toastTrigger(
        <div>
          <div className="S1">Failed to save session metadata.</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }
  };

  useEffect(() => {
    if (open) {
      setLocalMetadata(metadata || '');
      setSelectedSessions(sessions || []);
    } else {
      setLocalMetadata('');
      setSelectedSessions([]);
    }
  }, [open, metadata, sessions]);

  return (
    <>
      <Modal
        open={open}
        customSize={{
          height: 'auto',
          minHeight: '200px',
          width: '640px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
        }}
        onRequestClose={!isSaving ? handleClose : undefined}
        renderHeaderContent={() => {
          return (
            <Container background="transparent" padding="24px 40px 16px">
              <div className="flex ai-center jc-space-between">
                <Text tag="h4" className="H4 margin-b-4">
                  Session metadata
                </Text>
                <button className="clear-button padding-4 flex ai-center" onClick={!isSaving ? handleClose : undefined}>
                  <Icon name={ICONS.CLOSE} size={16} />
                </button>
              </div>
              <Text className="P1" color="var(--gray-400)">
                Add a searchable identifier for your session(s)
              </Text>
            </Container>
          );
        }}
        renderFooterContent={() => {
          return (
            <Container
              background="transparent"
              className="flex fd-row jc-flex-end ai-center gap-8"
              padding="0 40px 24px 40px"
            >
              <button onClick={onClose} disabled={isSaving} className="L1 padding-v-16 padding-h-24">
                Cancel
              </button>

              <Button
                backgroundColor={getThemeColorCSSVariable()}
                padding="16px 24px"
                width="auto"
                onClick={handleSave}
                disabled={!localMetadata?.trim() || !selectedSessions?.length || isSaving}
                disabledOpacity={0.6}
                hoverOpacity={0.8}
                loading={isSaving}
              >
                <Text
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                  className="L1"
                >
                  Save
                </Text>
              </Button>
            </Container>
          );
        }}
      >
        <Container background="transparent" padding="0 40px 24px 40px" className="flex-column gap-16">
          <SessionSelect
            label="Session"
            placeholder="Select sessions"
            sessions={project?.sessions || []}
            selectedSessionIds={selectedSessions}
            onChange={(ids) => setSelectedSessions(ids)}
            open={showSessionSelect}
            setOpen={setShowSessionSelect}
          />
          <div className="flex-column gap-2">
            <label className="margin-l-16 P2" color="var(--gray-400)">
              Session metadata
            </label>
            <TextAreaAutosize
              placeholder="Add session metadata"
              className="clear-textarea S3 width-100-percent radius-8"
              minRows={2}
              maxRows={2}
              maxLength={maxLength}
              disabled={isSaving}
              style={{
                border: '1px solid var(--gray-600)',
                padding: '14px 16px',
                borderRadius: '16px',
              }}
              onChange={handleChange}
              value={localMetadata}
            />
            <Text className="as-flex-end C2 margin-r-16" color="var(--gray-400)">
              {localMetadata?.length} / {maxLength}
            </Text>
          </div>
        </Container>
      </Modal>
    </>
  );
};

export default SessionMetadataModal;
